import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import ShipmentRecorder from './components/ShipmentRecorder';
import Login from './components/auth/Login';
import Signup from './components/auth/Signup';
import RechargeModal from './components/RechargeModal';
import SupabaseSetupGuide from './components/SupabaseSetupGuide';
import ProfilePage from './components/ProfilePage';
import BillingPage from './components/BillingPage';
import LandingPage from './components/LandingPage';
import { getSession, authSignOut, getCredits, addCredits, recordTransaction, onAuthStateChange } from './lib/auth';
import { isSupabaseConnected } from './lib/supabase';
import SafeIcon from './common/SafeIcon';
import ShipAssureLogo from './common/ShipAssureLogo';
import * as FiIcons from 'react-icons/fi';
import './App.css';

// ─── Protected Route ──────────────────────────────────────────────────────────
const ProtectedRoute = ({ children }) => {
  const [session, setSession] = useState(undefined);

  useEffect(() => {
    const loadSession = async () => {
      const { data } = await getSession();
      setSession(data?.session || null);
    };
    loadSession();
    if (isSupabaseConnected) {
      const { data: { subscription } } = onAuthStateChange((_event, s) => setSession(s));
      return () => subscription?.unsubscribe();
    }
  }, []);

  if (session === undefined) return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100">
      <div className="w-12 h-12 border-4 border-blue-600/30 border-t-blue-600 rounded-full animate-spin" />
    </div>
  );
  if (!session) return <Navigate to="/login" replace />;
  return children;
};

// ─── Settings Dropdown ────────────────────────────────────────────────────────
const SettingsMenu = ({ onLogout }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const go = (path) => { setOpen(false); navigate(path); };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(o => !o)}
        className={`flex items-center gap-1.5 text-sm font-semibold px-3 py-1.5 rounded-full border transition-all ${open ? 'bg-blue-50 border-blue-300 text-blue-700' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}
      >
        <SafeIcon icon={FiIcons.FiSettings} className={`transition-transform duration-300 ${open ? 'rotate-45' : ''}`} />
        <span className="hidden sm:inline">Settings</span>
        <SafeIcon icon={FiIcons.FiChevronDown} className={`text-xs transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -6, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -6, scale: 0.97 }}
            transition={{ duration: 0.15 }}
            className="absolute right-0 mt-2 w-48 bg-white rounded-xl shadow-lg border border-gray-100 py-1.5 z-50"
          >
            <button onClick={() => go('/app/profile')}
              className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2.5 transition-colors">
              <SafeIcon icon={FiIcons.FiUser} className="text-blue-500" /> My Profile
            </button>
            <button onClick={() => go('/app/billing')}
              className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2.5 transition-colors">
              <SafeIcon icon={FiIcons.FiCreditCard} className="text-green-500" /> Billing
            </button>
            <div className="border-t border-gray-100 my-1" />
            <button onClick={onLogout}
              className="w-full text-left px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2.5 transition-colors">
              <SafeIcon icon={FiIcons.FiLogOut} className="text-red-500" /> Logout
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ─── Layout ───────────────────────────────────────────────────────────────────
const Layout = ({ children }) => {
  const [user, setUser] = useState(null);
  const [credits, setCredits] = useState(0);
  const [showRecharge, setShowRecharge] = useState(false);
  const [rechargeToast, setRechargeToast] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const loadUser = async () => {
      const { data } = await getSession();
      const u = data?.session?.user || null;
      setUser(u);
      if (u) {
        const c = await getCredits(u.id);
        setCredits(c);
      }
    };
    loadUser();
  }, []);

  useEffect(() => {
    const interval = setInterval(async () => {
      if (user?.id) {
        const c = await getCredits(user.id);
        setCredits(c);
      }
    }, 3000);
    return () => clearInterval(interval);
  }, [user]);

  const handleLogout = async () => {
    await authSignOut();
    navigate('/');
    window.location.reload();
  };

  const handleRechargeSuccess = async (addedCredits, paymentId, planName, amountSpent) => {
    if (user?.id) {
      const newTotal = await addCredits(user.id, addedCredits);
      setCredits(newTotal || credits + addedCredits);
      await recordTransaction({
        userId: user.id,
        planName: planName || 'Unknown',
        paymentId: paymentId || '',
        creditsPurchased: addedCredits,
        amountSpent: amountSpent || 0,
      });
    }
    setRechargeToast(`✅ ${addedCredits} credits added! Payment ID: ${paymentId}`);
    setTimeout(() => setRechargeToast(''), 5000);
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <nav className="bg-white shadow-sm border-b px-4 py-3 flex justify-between items-center sticky top-0 z-40">
        {/* ── Logo ── */}
        <button
          onClick={() => navigate('/app')}
          className="flex items-center gap-3 hover:opacity-80 transition-opacity text-left"
        >
          <ShipAssureLogo size={40} />
          <div className="flex flex-col justify-center">
            <span className="font-extrabold tracking-tight text-2xl leading-none mb-1">
              <span className="text-gray-900">Ship</span>
              <span className="text-blue-600">Assure</span>
            </span>
            <span className="text-sm font-medium text-gray-500 leading-none">
              Turn Every Shipment into Verified Proof
            </span>
          </div>
        </button>

        {/* Right side */}
        <div className="flex items-center gap-2">
          {/* Credits pill */}
          <div className="hidden sm:flex items-center gap-1.5 bg-yellow-50 border border-yellow-200 px-3 py-1.5 rounded-full">
            <SafeIcon icon={FiIcons.FiZap} className="text-yellow-500 text-sm" />
            <span className="text-sm font-bold text-yellow-700">{credits} credits</span>
          </div>

          {/* Recharge */}
          <button
            onClick={() => setShowRecharge(true)}
            className="flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-bold px-3 py-1.5 rounded-full transition-colors shadow-sm"
          >
            <SafeIcon icon={FiIcons.FiPlus} />
            <span className="hidden sm:inline">Recharge</span>
          </button>

          {/* Settings dropdown */}
          <SettingsMenu onLogout={handleLogout} />
        </div>
      </nav>

      {rechargeToast && (
        <div className="fixed top-16 left-1/2 -translate-x-1/2 z-50 bg-green-600 text-white text-sm font-semibold px-5 py-3 rounded-xl shadow-lg flex items-center gap-2">
          {rechargeToast}
        </div>
      )}

      {children}

      <RechargeModal
        isOpen={showRecharge}
        onClose={() => setShowRecharge(false)}
        onSuccess={handleRechargeSuccess}
        userEmail={user?.email}
      />
    </div>
  );
};

// ─── App ──────────────────────────────────────────────────────────────────────
function App() {
  return (
    <Router>
      <Routes>
        {/* Public routes */}
        <Route path="/"        element={<LandingPage />} />
        <Route path="/login"   element={<Login />} />
        <Route path="/signup"  element={<><Signup /><SupabaseSetupGuide /></>} />

        {/* Protected app routes */}
        <Route path="/app" element={
          <ProtectedRoute>
            <Layout>
              <ShipmentRecorder />
            </Layout>
          </ProtectedRoute>
        } />
        <Route path="/app/profile" element={
          <ProtectedRoute>
            <Layout>
              <ProfilePage />
            </Layout>
          </ProtectedRoute>
        } />
        <Route path="/app/billing" element={
          <ProtectedRoute>
            <Layout>
              <BillingPage />
            </Layout>
          </ProtectedRoute>
        } />

        {/* Fallback */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Router>
  );
}

export default App;